import { CityProfile, ICityProfile } from '@app/models/CityProfile'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum CitiesScope {
  All = 'All',
  Created = 'Created',
  Mine = 'Mine',
}

export interface ICitiesListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<ICityProfile>
  readonly scope: CitiesScope
  readonly sortOrder: string
  readonly totalItems: number
}

const CitiesListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<CityProfile>(),
  scope: CitiesScope.Created,
  sortOrder: 'LastName',
  totalItems: 0,
})

export class CitiesListState extends CitiesListStateRecord
  implements ICitiesListState {
  public static fromJS(initialState?: Partial<ICitiesListState>) {
    if (!initialState) {
      return new CitiesListState()
    }

    const { items, ...rest } = initialState
    return new CitiesListState({
      items: items ? List(items.map((i) => CityProfile.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<CityProfile>
  public readonly scope: CitiesScope
  public readonly sortOrder: string
  public readonly totalItems: number

  constructor(props?: Partial<CitiesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum CitiesListActionTypes {
  LoadCities = 'CITIES_LIST::LOAD_CITIES',
  LoadCitiesFailure = 'CITIES_LIST::LOAD_CITIES_FAILURE',
  LoadCitiesRequest = 'CITIES_LIST::LOAD_CITIES_REQUEST',
  LoadCitiesSuccess = 'CITIES_LIST::LOAD_CITIES_SUCCESS',
  SetFilterText = 'CITIES_LIST::SET_FILTER_TEXT',
  SetScope = 'CITIES_LIST::SET_SCOPE',
  SetSortOrder = 'CITIES_LIST::SET_SORT_ORDER',
}

interface ILoadCitiesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: CitiesListActionTypes.LoadCities
}

interface ILoadCitiesFailureAction {
  error: string
  type: CitiesListActionTypes.LoadCitiesFailure
}

interface ILoadCitiesRequestAction {
  type: CitiesListActionTypes.LoadCitiesRequest
}

interface ILoadCitiesSuccessAction {
  append: boolean
  items: CityProfile[]
  totalItems: number
  type: CitiesListActionTypes.LoadCitiesSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: CitiesListActionTypes.SetFilterText
}

interface ISetScopeAction {
  scope: CitiesScope
  type: CitiesListActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: CitiesListActionTypes.SetSortOrder
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadCitiesFailureAction
  | ILoadCitiesRequestAction
  | ILoadCitiesSuccessAction
  | ISetFilterTextAction
  | ISetScopeAction
  | ISetSortOrderAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadCities(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadCitiesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: CitiesListActionTypes.LoadCities,
  }
}

function loadCitiesFailure(error: string): ILoadCitiesFailureAction {
  return {
    error,
    type: CitiesListActionTypes.LoadCitiesFailure,
  }
}

function loadCitiesRequest(): ILoadCitiesRequestAction {
  return {
    type: CitiesListActionTypes.LoadCitiesRequest,
  }
}

function loadCitiesSuccess(
  items: CityProfile[],
  append: boolean,
  totalItems: number,
): ILoadCitiesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: CitiesListActionTypes.LoadCitiesSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: CitiesListActionTypes.SetFilterText,
  }
}

function setScope(scope: CitiesScope): ISetScopeAction {
  return {
    scope,
    type: CitiesListActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: CitiesListActionTypes.SetSortOrder,
  }
}

export const actionCreators = {
  loadCities,
  setFilterText,
  setScope,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<CitiesListState> = (
  state: CitiesListState = new CitiesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case CitiesListActionTypes.LoadCitiesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as CitiesListState

    case CitiesListActionTypes.LoadCitiesRequest:
      return state.set('isLoading', true) as CitiesListState

    case CitiesListActionTypes.LoadCitiesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<CityProfile>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as CitiesListState

    case CitiesListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as CitiesListState

    case CitiesListActionTypes.SetScope:
      return state.set('scope', action.scope) as CitiesListState

    case CitiesListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as CitiesListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadCitiesSaga(action: ILoadCitiesAction) {
  try {
    yield put(loadCitiesRequest())

    const currentState: CitiesListState = yield select(
      (state: ApplicationState) => state.citiesList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/cityProfiles'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: CityProfile[] = result.results.map((c: any) =>
        CityProfile.fromJS(c),
      )
      yield put(
        loadCitiesSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadCitiesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadCitiesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadCities() {
  yield takeLatest(CitiesListActionTypes.LoadCities, loadCitiesSaga)
}

export function* citiesListRootSaga() {
  yield fork(watchLoadCities)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): CitiesListState =>
  state.citiesList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectSortOrder = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.sortOrder,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
